/**
 * ====================================================================
 * WeLearn Pro Player
 * This script is a wrapper for Plyr player (https://plyr.io) for customize
 * events those will present in WeLearn Pro course video pages.
 *
 * Copyright© 2019 Wewasanad Co.,Ltd. All rights reserved.
 *
 * --------------------------------------------------------------------
 * Version 1.3.0.0 - 2020-09-30
 * * Add changelog
 * * Add default Plyr settings for learning and preview mode
 * * Plyr settings: Speed control is disabled by default
 * ====================================================================
 */


// Use strict mode for better performance.
'use strict';

// Import helpers and components
import defaults from './config/defaults.js';
import { extend } from './util/objectHelper';
import Bootstrap4Helper from "./util/bootstrapHelper.js";
import Debug from './util/debugHelper.js'
import Note from './note.js';
import Caption from './caption.js';
import Slide from './slide.js';
import Loader from './loader.js';
import UI from './ui.js';
import Quiz from './quiz';
import Events from './events';
import Chat from './chat';
import { plyrControls, plyrSettings, plyrWlpOptions } from './config/plyr-defaults.js'
import { wlpDefaultsOptions } from './config/wlp-defaults.js'

/**
 * WeLearn Pro Video Player class.
 */
class WeLearnProPlayer {
    //#debug = {};

    /**
     * WeLearn Pro Video Player class.
     * @param element - JQuery selector string of HTML5 video tag including # (Must define)
     * @param options - WeLearn Pro Player options (Optional)
     * @param progressData - User's video progress data (Optional)
     * @param videoComponent - WLP video components (Slide, test, etc)
     * @param plyrOptions - Plyr options
     */
    constructor(element, options = {}, progressData = {}, videoComponent = {}, plyrOptions = undefined) {
        this.element = element;
        this.options = extend ({}, defaults, wlpDefaultsOptions, options || {});
        this.plyrControls = plyrControls;
        this.plyrSettings = plyrSettings;
        this.progressData = progressData;
        this.videoComponent = videoComponent;
        this.syncSlide = this.options.slidesync;

        // Throw error if element is not defined.
        if (!element.length) {
            throw "Undefined, unknown or null HTML video element. Please specify the name of element.";
        }

        // Instantiate associated components.
        this.chat = new Chat(this);
        this.note = new Note(this);
        this.caption = new Caption(this);
        this.slide = new Slide(this);
        this.loader = new Loader(this);
        this.ui = new UI(this);
        this.quiz = new Quiz(this);
        this.event = {}; // Event will be instantiated after binding Plyr instance to element.
        this.debug = new Debug(this.options.debug);

        // Show debug message
        this.debug.log("WLP Player: Debug mode enabled.");
        this.debug.log("===== WLP Player settings =====");
        this.debug.log(this.options);
        this.debug.log(this.progressData);
        this.debug.log(this.videoComponent);
        this.debug.log("===============================");

        this.loader.initVideo();
        this.bindPlyr(plyrOptions);
    }

    /**
     * Get pass status of user of this video
     * @returns {boolean} - true = passed, false = not passed.
     */
    get isPassByPercentage() {
        return this.progressPercentage > this.progressData.passConditions.watchedPercentage;
    }

    /**
     * Get watched progress in percentage
     * @returns {number} - Watched progress value in percentage
     */
    get progressPercentage() {
        let watchedSeconds = $('span.tick.watched').length;
        return (watchedSeconds * 100) / Math.floor(this.plyrPlayer.duration);
    }

    /** =====================================
     *
     *  Video options
     *
     * ======================================
     */

    /**
     * Get available captions of the video
     * @returns {object} - Caption list
     */
    get availableCaptions() {
        let captions = this.progressData["caption"];
        let languages = {};

        for (let i = 0; i < captions.length; i++) {
            languages[i] = captions[i]["label"];
        }

        return languages;
    }

    /**
     * Bind Plyr player to default HTML video player. This let user access Plyr functionality.
     * @param options - Plyr options
     */
    bindPlyr(options) {

        if (!options) {
            options = plyrWlpOptions;
        }

        if (!options.controls || options.controls.length === 0) {
            if (this.options.previewMode === true) {
                options.controls = this.plyrControls.previewModeControls;
            } else {
                options.controls = this.plyrControls.learningModeControls;
            }
        }

        if (!options.settings || options.settings.length === 0) {
            if (this.options.previewMode === true) {
                options.settings = this.plyrSettings.previewModeSettings;
            } else {
                options.settings = this.plyrSettings.learningModeSettings;
            }
        }

        this.plyrPlayer = new Plyr(this.element, options);
        this.event = new Events(this);

        $(this.element).addClass('hytPlayerWrap');
    }

    /** =====================================
     *
     *  Note
     *
     * ======================================
     */

    /**
     * Delete an instance of WeLearn Pro Player
     * @deprecated Please use default Javascript feature to dispose.
     */
    dispose() {
        delete this;
    }

    /**
     * Set this video can be seek or not.
     * @param canSeek - true and false
     */
    seekablePlyrProgressBar(canSeek) {
        if (canSeek === true) {
            $('.plyr__progress').css({ 'pointer-events': 'auto' });
            return true;
        } else {
            $('.plyr__progress').css('pointer-events', 'none');
            return false;
        }
    }

    /** =====================================
     *
     *  Caption
     *
     * ======================================
     */

    captureThumbnail() {
        $('body').append($('<canvas id="tempCanvas"></canvas>'));
        let canvas = document.getElementById('tempCanvas');
        let video = this.element;
        canvas.getContext('2d').drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
        canvas.remove();
    }

    /** =====================================
     *
     *  Slide
     *
     * ======================================
     */

    /**
     * Create note at the current time of video.
     * @param time
     * @param text
     */
    createNote(text, time = this.plyrPlayer.currentTime) {
        return this.note.createNote(text, time);
    }

    /**
     * Seek video to specific time.
     * @param time - Time of video.
     * @param useSeekableOption - Apply 'seekable' rule from video option.
     */
    seekTo(time, useSeekableOption) {
        return this.note.seekTo(time, useSeekableOption);
    }

    /** =====================================
     *
     *  Quiz
     *
     * ======================================
     */

    /**
     * Add caption to video (Must be load before the player finished initialize)
     * @param label - Language name
     * @param srclang - Language abbreviation
     * @param src - Caption file path
     * @param setDefault - Set to true if this language is default language or vice versa
     */
    addCaption(label, srclang, src, setDefault = false) {
        return this.caption.addCaption(label, srclang, src, setDefault);
    }

    /**
     * Change slide page to specify page.
     * @param slidePage - Slide page to change to.
     */
    changeSlidePage(slidePage) {
        return this.slide.changeSlidePage(slidePage);
    }

    /** =====================================
     *
     *  Getters
     *
     * ======================================
     */

    /**
     * Toggle sync slide
     */
    toggleSyncSlide() {
        if (this.syncSlide === undefined) {
            this.syncSlide = false;
        }

        return this.syncSlide = this.syncSlide ? this.slide.unsyncSlide() : this.slide.syncSlide();
    }

    /**
     * Invoke quiz by quiz ID.
     * @param quizId - Quiz ID
     */
    invokeQuiz(quizId) {
        return this.quiz.invokeQuiz(quizId);
    }

    /**
     * Unload quiz from screen.
     */
    unloadQuiz() {
        return this.quiz.unloadQuiz();
    }

    /** =====================================
     *
     *  Wrappers
     *
     * ======================================
     */

    /**
     * Create alert that appear under WeLearn Pro Player. (This is a wrapper of BootstrapHelper)
     * @param message - Message to show.
     * @param timeout - Time to close alert.
     */
    invokeAlert(message, timeout) {
        Bootstrap4Helper.invokeAlert(message, timeout, '.wlpVideo__progress-bar');
    }
}
